// components/industry-templates/DataScienceEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Brain, BarChart3, Code, Award } from 'lucide-react';

export const DataScienceEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* ML/AI Workshops */}
      {industryData?.ml_workshops && (
        <Card className="border-0 bg-gradient-to-br from-purple-600/20 to-blue-600/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Brain className="w-8 h-8" style={{ color: theme.primary }} />
              Machine Learning Workshops
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {industryData.ml_workshops.map((workshop: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-2">{workshop.topic}</h3>
                  <p className="text-slate-300 text-sm mb-3">{workshop.description}</p>
                  <div className="flex gap-2 flex-wrap">
                    <Badge style={{ backgroundColor: theme.primary }}>{workshop.instructor}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">{workshop.level}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">{workshop.duration}</Badge>
                  </div>
                  {workshop.tools_used && (
                    <div className="mt-3 flex gap-2">
                      {workshop.tools_used.map((tool: string, i: number) => (
                        <Badge key={i} className="bg-blue-500 text-xs">{tool}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Science Competition */}
      {industryData?.kaggle_competition && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Data Science Competition
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-white/10 rounded-xl p-6 text-center">
                <p className="text-5xl font-bold mb-2" style={{ color: theme.primary }}>
                  {industryData.kaggle_competition.prize_pool}
                </p>
                <p className="text-white text-xl mb-2">Prize Pool</p>
                <p className="text-slate-400">{industryData.kaggle_competition.challenge}</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-2xl font-bold text-white">{industryData.kaggle_competition.dataset_size}</p>
                  <p className="text-xs text-slate-400">Dataset Size</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-2xl font-bold text-white">{industryData.kaggle_competition.duration}</p>
                  <p className="text-xs text-slate-400">Duration</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-2xl font-bold text-white">{industryData.kaggle_competition.team_size}</p>
                  <p className="text-xs text-slate-400">Team Size</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tools & Frameworks */}
      {industryData?.tools_showcase && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Code className="w-6 h-6" style={{ color: theme.primary }} />
              Data Science Tools & Frameworks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {industryData.tools_showcase.map((tool: string, idx: number) => (
                <div key={idx} className="p-3 bg-white/5 rounded-lg text-center border border-white/10 hover:scale-105 transition-transform">
                  <div className="text-2xl mb-2">🔧</div>
                  <p className="text-white font-medium text-xs">{tool}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Use Cases */}
      {industryData?.industry_use_cases && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-6 h-6" style={{ color: theme.primary }} />
              Industry Use Cases
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.industry_use_cases.map((useCase: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-1">{useCase.title}</h4>
                  <p className="text-sm text-slate-400 mb-2">{useCase.industry}</p>
                  <Badge style={{ backgroundColor: theme.primary }}>{useCase.impact}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
